import { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Trash2 } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import toast from 'react-hot-toast';
import { useDevOpsStore } from '../../../store/devopsStore';

const EMPTY_REPO = { name: '', provider: 'github', url: '', branch: 'main' };

export function DevOpsDashboard() {
    const { repositories, pipelines, finopsSummary, fetchRepositories, fetchPipelines, fetchFinOpsSummary, loading, syncRepository, connectRepository, deleteRepository } = useDevOpsStore();
    const [isFormOpen, setFormOpen] = useState(false);
    const [repo, setRepo] = useState(EMPTY_REPO);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchRepositories();
        fetchPipelines();
        fetchFinOpsSummary();
    }, []);

    const openConnect = () => { setRepo(EMPTY_REPO); setErrors({}); setFormOpen(true); };

    const handleConnect = async () => {
        const next: Record<string, string> = {};
        if (!repo.name.trim()) next.name = 'Name is required';
        if (!repo.url.trim()) next.url = 'Repository URL is required';
        else if (!/^https?:\/\/.+/i.test(repo.url.trim())) next.url = 'Enter a valid URL';
        setErrors(next);
        if (Object.keys(next).length) return;
        setSaving(true);
        try {
            await connectRepository({ name: repo.name.trim(), provider: repo.provider, url: repo.url.trim(), branch: repo.branch.trim() || 'main' });
            toast.success('Repository connected');
            setFormOpen(false);
        } catch (e: any) {
            toast.error(e.response?.data?.error || 'Failed to connect repository');
        } finally {
            setSaving(false);
        }
    };

    const handleSync = async (id: string) => {
        const res = await syncRepository(id);
        if (res?.success) {
            toast.success(res.message || 'Repository synced');
            fetchRepositories();
        } else {
            toast.error(res?.message || 'Sync failed');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteRepository(deleteTarget.id);
            toast.success('Repository removed');
            setDeleteTarget(null);
        } catch {
            toast.error('Failed to remove repository');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">DevOps <span className="text-foreground">Center</span></h1>
                    <p className="text-muted-foreground mt-1">CI/CD pipelines, code activity, and Cloud/FinOps</p>
                </div>
                <Button onClick={() => fetchRepositories()} variant="secondary" size="sm">Refresh</Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Repositories */}
                <Card className="lg:col-span-2">
                    <CardHeader className="bg-card/30 px-6 py-4 flex justify-between items-center">
                        <h2 className="text-foreground flex items-center gap-2 font-display text-section-title">Connected Repositories</h2>
                        <Button size="xs" variant="primary" onClick={openConnect}>+ Connect Repo</Button>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading && <div className="p-8 text-center text-muted-foreground animate-pulse">Scanning infrastructure...</div>}
                        {!loading && repositories.length === 0 && (
                            <div className="p-12 text-center">
                                <p className="text-muted-foreground">No repositories connected.</p>
                                <Button className="mt-4" variant="secondary" size="sm" onClick={openConnect}>Connect a Repository</Button>
                            </div>
                        )}
                        <table className="w-full text-sm">
                            <tbody className="divide-y divide-border-12">
                                {repositories.map((repo) => (
                                    <tr key={repo.id} className="hover:bg-card/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center font-semibold text-primary">
                                                    {repo.provider === 'github' ? 'GH' : 'GL'}
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-foreground">{repo.name}</p>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className="text-xs bg-background border border-border px-2 py-0.5 rounded-md font-mono text-primary">{repo.branch}</span>
                                                        <span className="text-xs text-muted-foreground">{repo.url}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button size="xs" variant="secondary" onClick={() => handleSync(repo.id)}>Sync</Button>
                                                <Button size="xs" variant="ghost" className="text-muted-foreground hover:text-destructive"
                                                    onClick={() => setDeleteTarget({ id: repo.id, name: repo.name })} aria-label="Remove repository">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>

                {/* Cloud Costs / FinOps */}
                <Card>
                    <CardHeader className="bg-card/30 px-6 py-4">
                        <h2 className="text-foreground flex items-center gap-2 font-display text-section-title">FinOps Summary (AWS)</h2>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="text-center mb-6">
                            <p className="text-xs font-semibold text-muted-foreground mb-2">Month to Date</p>
                            <p className="text-warning font-display text-metric">{finopsSummary?.total || '$0'}</p>
                            <p className="text-xs text-muted-foreground mt-2">{finopsSummary?.trend !== 'N/A' ? `▲ ${finopsSummary?.trend}` : 'No trend data'}</p>
                        </div>
                        <div className="space-y-4 pt-4 border-t border-border">
                            {finopsSummary?.details && finopsSummary.details.length > 0 ? (
                                finopsSummary.details.map(item => (
                                    <div key={item.label} className="flex justify-between items-center text-sm">
                                        <span className="text-muted-foreground">{item.label}</span>
                                        <span className="font-semibold text-foreground">{item.val}</span>
                                    </div>
                                ))
                            ) : (
                                <p className="text-xs text-muted-foreground text-center">
                                    Cloud cost integration not configured. Connect a billing source to see FinOps data.
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pipeline Status */}
            <Card>
                    <CardHeader className="bg-card/30 px-6 py-4 flex justify-between items-center">
                        <h2 className="text-foreground flex items-center gap-2 font-display text-section-title">Recent Deployments</h2>
                        <Badge variant="primary">{pipelines.length} Active Pipelines</Badge>
                    </CardHeader>
                    <CardContent className="p-0">
                        <table className="w-full text-sm">
                            <tbody className="divide-y divide-border-12">
                                {pipelines.length === 0 ? (
                                    <tr><td colSpan={2} className="text-center p-8 text-muted-foreground">No recent deployments</td></tr>
                                ) : pipelines.map((p, i) => (
                                    <tr key={i} className="hover:bg-card/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-semibold text-lg ${p.status === 'success' ? 'bg-success/10 text-success' :
                                                    p.status === 'failed' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary rotate-180 animate-spin'
                                                    }`}>
                                                    {p.status === 'success' ? '' : p.status === 'failed' ? '' : ''}
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-foreground">{p.project}</p>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className="text-xs bg-background border border-border px-2 py-0.5 rounded-md font-mono text-primary">{p.branch}</span>
                                                        <span className="text-xs text-muted-foreground">by {p.author}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <p className="text-sm font-semibold text-muted-foreground">{p.duration}</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">{p.time}</p>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>

            {/* Connect repository modal */}
            <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Connect Repository</DialogTitle>
                        <DialogDescription>Register a Git repository for this workspace.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Name</label>
                            <Input value={repo.name} onChange={(e) => setRepo(r => ({ ...r, name: e.target.value }))}
                                placeholder="e.g. teamone-frontend" invalid={!!errors.name} />
                            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Provider</label>
                                <Select value={repo.provider} onValueChange={(v) => setRepo(r => ({ ...r, provider: v }))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="github">GitHub</SelectItem>
                                        <SelectItem value="gitlab">GitLab</SelectItem>
                                        <SelectItem value="bitbucket">Bitbucket</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Default Branch</label>
                                <Input value={repo.branch} onChange={(e) => setRepo(r => ({ ...r, branch: e.target.value }))} placeholder="main" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Repository URL</label>
                            <Input value={repo.url} onChange={(e) => setRepo(r => ({ ...r, url: e.target.value }))}
                                placeholder="https://github.com/org/repo" invalid={!!errors.url} />
                            {errors.url && <p className="text-xs text-destructive">{errors.url}</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
                        <Button variant="primary" onClick={handleConnect} isLoading={saving}>Connect</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Remove repository</DialogTitle>
                        <DialogDescription>
                            Remove <span className="font-medium text-foreground">{deleteTarget?.name}</span> from this workspace? This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} isLoading={deleting}>Remove</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}



